'use client';

import { c as _c } from "react/compiler-runtime";
import Image from 'next/image';
import { useCallback, useEffect, useState } from 'react';
import { useClient, useQuery } from 'urql';
import { Birds } from '@/graphql/queries';
import type { BirdsQuery, BirdsQueryVariables, League } from '@/gql/graphql';
import { copyTextToClipboard } from '@tma.js/sdk';
import clsx from 'clsx';
import { PAGE_SIZE, useInfiniteList } from '@/hooks/useInfiniteList';
const leagueBadgeClasses: Record<string, string> = {
  Nestling: 'bg-gradient-to-r from-nestling/50 to-nestling text-foreground',
  Junior: 'bg-gradient-to-r from-junior/50 to-junior text-foreground',
  Master: 'bg-gradient-to-r from-master/50 to-master text-foreground',
  Expert: 'bg-gradient-to-r from-expert/50 to-expert text-foreground',
  Pro: 'bg-gradient-to-r from-pro/50 to-pro text-foreground',
  Plus: 'bg-gradient-to-r from-plus/50 to-plus text-foreground',
  Bronze: 'bg-gradient-to-r from-bronze/50 to-bronze text-foreground',
  Silver: 'bg-gradient-to-r from-silver/50 to-silver text-foreground',
  Gold: 'bg-gradient-to-r from-gold/50 to-gold text-foreground',
  Platinum: 'bg-gradient-to-r from-platinum/50 to-platinum text-foreground',
  Diamond: 'bg-gradient-to-r from-diamond/50 to-diamond text-foreground',
  Premium: 'bg-gradient-to-r from-premium/50 to-premium text-foreground',
  Feathered: 'bg-gradient-to-r from-feathered/50 to-feathered text-foreground'
} as const;
const leaguePills: {
  value: League | null;
  label: string;
}[] = [{
  value: null,
  label: 'All'
}, ...(['Feathered', 'Premium', 'Diamond', 'Platinum', 'Gold', 'Silver', 'Bronze', 'Pro', 'Plus', 'Expert', 'Master', 'Junior', 'Nestling'] as const).map(league => ({
  value: league,
  label: league
}) as const)];
interface Props {
  onClose: () => void;
}
export default function TopBirdsModal(t0) {
  const $ = _c(54);
  const {
    onClose
  } = t0;
  const [isVisible, setIsVisible] = useState(false);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const client = useClient();
  let t1;
  if ($[0] !== selectedLeague) {
    t1 = offset => ({
      pagination: {
        limit: PAGE_SIZE,
        offset
      },
      league: selectedLeague || undefined
    }) satisfies BirdsQueryVariables;
    $[0] = selectedLeague;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== client || $[3] !== t1) {
    t2 = {
      client,
      query: Birds,
      variablesFn: t1,
      getItems: _temp
    };
    $[2] = client;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    items: birds,
    isLoadingMore,
    hasMore,
    scrollRef,
    sentinelRef,
    reset
  } = useInfiniteList(t2);
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      limit: PAGE_SIZE,
      offset: 0
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = selectedLeague || undefined;
  let t5;
  if ($[6] !== t4) {
    t5 = {
      query: Birds,
      variables: {
        pagination: t3,
        league: t4
      }
    };
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const [birdsResult] = useQuery(t5);
  let t6;
  if ($[8] !== birdsResult.data?.birds || $[9] !== reset) {
    t6 = () => {
      const fresh = birdsResult.data?.birds;
      if (fresh) {
        reset(fresh);
      }
    };
    $[8] = birdsResult.data?.birds;
    $[9] = reset;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = birdsResult.data?.birds;
  let t8;
  if ($[11] !== reset || $[12] !== selectedLeague || $[13] !== t7) {
    t8 = [t7, selectedLeague, reset];
    $[11] = reset;
    $[12] = selectedLeague;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  useEffect(t6, t8);
  let t9;
  if ($[15] !== onClose) {
    t9 = () => {
      setIsVisible(false);
      setTimeout(onClose, 300);
    };
    $[15] = onClose;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const handleClose = t9;
  let t10;
  let t11;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      requestAnimationFrame(() => setIsVisible(true));
    };
    t11 = [];
    $[17] = t10;
    $[18] = t11;
  } else {
    t10 = $[17];
    t11 = $[18];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[19] !== isVisible) {
    t12 = () => {
      if (!isVisible) {
        return;
      }
      const html = document.documentElement;
      const original = html.style.overflow;
      html.style.overflow = "hidden";
      return () => {
        html.style.overflow = original;
      };
    };
    t13 = [isVisible];
    $[19] = isVisible;
    $[20] = t12;
    $[21] = t13;
  } else {
    t12 = $[20];
    t13 = $[21];
  }
  useEffect(t12, t13);
  const t14 = isVisible ? "opacity-100" : "opacity-0";
  let t15;
  if ($[22] !== t14) {
    t15 = clsx("fixed inset-0 z-200 flex items-end backdrop-blur-lg transition-opacity duration-300 ease-out", t14);
    $[22] = t14;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  const t16 = isVisible ? "translate-y-0" : "translate-y-full";
  let t17;
  if ($[24] !== t16) {
    t17 = clsx("max-h-[85vh] bg-surface w-full rounded-t-4xl overflow-auto flex flex-col transition-transform duration-300 ease-out", t16);
    $[24] = t16;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  let t18;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <h2 className="text-2xl font-semibold">Top Birds</h2>;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  let t19;
  if ($[27] !== onClose) {
    t19 = <div className="p-6 border-b flex items-center justify-between">{t18}<Image src="/icon-close.svg" alt="Close" width={32} height={32} onClick={onClose} className="cursor-pointer" /></div>;
    $[27] = onClose;
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  let t20;
  if ($[29] !== selectedLeague) {
    t20 = leaguePills.map(pill => <button key={pill.value ?? "all"} onClick={() => setSelectedLeague(pill.value)} className={clsx("px-5 py-2 text-sm font-medium rounded-3xl whitespace-nowrap snap-start transition-all", selectedLeague === pill.value ? leagueBadgeClasses[pill.value ?? ""] || "bg-primary text-foreground" : "bg-background text-muted hover:bg-surface")}>{pill.label}</button>);
    $[29] = selectedLeague;
    $[30] = t20;
  } else {
    t20 = $[30];
  }
  let t21;
  if ($[31] !== t20) {
    t21 = <div className="px-6 pt-3 pb-3 border-b"><div className="flex gap-2 overflow-x-auto snap-x snap-mandatory scrollbar-hide">{t20}</div></div>;
    $[31] = t20;
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  let t22;
  if ($[33] !== birds) {
    t22 = birds.map(_temp3);
    $[33] = birds;
    $[34] = t22;
  } else {
    t22 = $[34];
  }
  let t23;
  if ($[35] !== isLoadingMore) {
    t23 = isLoadingMore && <div className="flex justify-center py-6"><span className="text-muted animate-pulse">Loading top birds...</span></div>;
    $[35] = isLoadingMore;
    $[36] = t23;
  } else {
    t23 = $[36];
  }
  let t24;
  if ($[37] !== hasMore || $[38] !== sentinelRef) {
    t24 = hasMore && <div ref={sentinelRef} className="h-6" />;
    $[37] = hasMore;
    $[38] = sentinelRef;
    $[39] = t24;
  } else {
    t24 = $[39];
  }
  let t25;
  if ($[40] !== scrollRef || $[41] !== t22 || $[42] !== t23 || $[43] !== t24) {
    t25 = <div ref={scrollRef} className="flex-1 overflow-auto px-6 py-4 space-y-3">{t22}{t23}{t24}</div>;
    $[40] = scrollRef;
    $[41] = t22;
    $[42] = t23;
    $[43] = t24;
    $[44] = t25;
  } else {
    t25 = $[44];
  }
  let t26;
  if ($[45] !== t17 || $[46] !== t19 || $[47] !== t21 || $[48] !== t25) {
    t26 = <div className={t17} onClick={_temp2}>{t19}{t21}{t25}</div>;
    $[45] = t17;
    $[46] = t19;
    $[47] = t21;
    $[48] = t25;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  let t27;
  if ($[50] !== handleClose || $[51] !== t15 || $[52] !== t26) {
    t27 = <div className={t15} onClick={handleClose}>{t26}</div>;
    $[50] = handleClose;
    $[51] = t15;
    $[52] = t26;
    $[53] = t27;
  } else {
    t27 = $[53];
  }
  return t27;
}
function _temp3(bird) {
  return <div key={bird.uuid} onClick={() => copyTextToClipboard(`Bird: ${bird.firstName} ${bird.lastName}\nUUID: ${bird.uuid}\nUsername: ${bird.username}`)} className="flex items-center gap-4 bg-background rounded-3xl p-4 cursor-pointer active:scale-90 transition-transform"><div className="relative size-14 rounded-full overflow-hidden border border-primary/30 shrink-0">{bird.premium && <div className="absolute inset-0 z-150 rounded-full bg-linear-to-b from-transparent to-gold/50 animate-pulse" />}<Image src={`/avatars/${bird.avatar?.toLowerCase()}.png`} alt={bird.username} fill={true} className="object-contain p-2 z-175" /></div><div className="flex-1 min-w-0"><p className="text-sm truncate">{bird.firstName} {bird.lastName}</p><p className="text-muted truncate">@{bird.username}</p><div className={clsx("mt-2 py-2 text-xs text-center font-semibold rounded-full whitespace-nowrap", leagueBadgeClasses[bird.league])}>{bird.league}</div></div><div className="text-right"><div className="flex items-center gap-1 justify-end"><span className="text-2xl font-bold">{bird.wallet.balance}</span><span className="text-xl text-primary">FTH</span></div></div></div>;
}
function _temp2(e) {
  return e.stopPropagation();
}
function _temp(data) {
  return data?.birds ?? [];
}